<template>
  <!-- App bar: indigo-lighten-2 background, flat with subtle bottom border -->
  <v-app-bar :elevation="0" color="grey-darken-4" density="comfortable" border="b thin opacity-25">
    <!-- Hamburger / close toggle (authenticated pages only) -->
    <v-btn
      icon
      variant="text"
      @click.stop="drawer = !drawer"
      v-if="isAuthenticated && $route.name !== 'NoAccess' && $route.name !== 'AccessDenied'"
    >
      <v-icon>{{ drawer ? "mdi-close" : "mdi-menu" }}</v-icon>
    </v-btn>

    <!-- Spacer for unauthenticated pages -->
    <div v-if="!isAuthenticated" class="ml-4" />

    <!-- Petty Cash sub-view title (ledger, employees, journal) -->
    <v-app-bar-title
      v-if="routeAppIs('pettycash') && isPettyCashRoute && pettyCashView !== 'daybook'"
    >
      <v-icon size="small" style="margin-right: 2px">{{ pettyCashViewIcon }}</v-icon
      ><template v-if="!smAndDown"
        ><span>{{ pettyCashViewTitle }}</span></template
      >
    </v-app-bar-title>

    <!-- Standard app title -->
    <v-app-bar-title v-else-if="$route.name !== 'NoAccess' && $route.name !== 'AccessDenied'">
      <v-icon
        v-if="currentAppConfig?.icon"
        size="small"
        :icon="currentAppConfig.icon"
        :color="currentAppConfig.iconColor"
        style="margin-right: 2px"
      /><template v-if="!(smAndDown && (isPettyCashRoute || isWarehouseRoute))"
        ><span :class="currentAppConfig?.iconColor ? `text-${currentAppConfig.iconColor}` : ''">{{
          title
        }}</span></template
      >
      <!-- Audit log total count chip — injected by AuditLogs.vue -->
      <v-chip
        v-if="route.name === 'AuditLogs' && auditTotalLogs > 0"
        size="x-small"
        variant="tonal"
        color="grey-darken-1"
        class="ml-2"
      >
        {{ auditTotalLogs.toLocaleString() }}
      </v-chip>
      <!-- Sales analytics record chip — injected by SalesAnalytics.vue via useAppBarActions -->
      <v-chip
        v-if="salesAnalyticsChip && isSalesRoute"
        size="x-small"
        :color="salesAnalyticsChip.chipColor || 'primary'"
        :variant="salesAnalyticsChip.chipVariant || 'tonal'"
        class="ml-2"
      >
        <v-icon start size="x-small">mdi-database</v-icon>
        {{ salesAnalyticsChip.chip }}
      </v-chip>
    </v-app-bar-title>

    <template v-slot:append>
      <template v-if="$route.name !== 'NoAccess' && $route.name !== 'AccessDenied'">
        <!-- Settings back button — shown on settings sub-pages for quick navigation back to Settings -->
        <div class="d-inline-block" v-if="isSettingsRoute && route.name !== 'Settings'">
          <v-btn icon variant="text" @click="router.push('/settings')">
            <v-icon color="grey-lighten-1">mdi-cog</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">Settings</v-tooltip>
        </div>

        <!-- Warehouse month picker — centered, shown on all warehouse pages -->
        <div
          v-if="routeAppIs('warehouse') && isWarehouseRoute"
          style="position: absolute; left: 50%; transform: translateX(-50%); z-index: 1"
        >
          <WarehouseMonthPicker />
        </div>

        <!-- Petty Cash month picker — centered, shown on all petty cash pages -->
        <div
          v-if="routeAppIs('pettycash') && isPettyCashRoute"
          style="position: absolute; left: 50%; transform: translateX(-50%); z-index: 1"
        >
          <PettyCashMonthPicker />
        </div>

        <!-- Warehouse home button — shown on warehouse sub-pages for quick navigation back -->
        <div
          class="d-inline-block"
          v-if="routeAppIs('warehouse') && isWarehouseRoute && route.name !== 'WarehouseInventory'"
        >
          <v-btn icon variant="text" @click="router.push('/accounting/warehouse')">
            <v-icon color="green-darken-3">mdi-warehouse</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">Warehouse Home</v-tooltip>
        </div>

        <!-- Reviews home button — shown on reviews sub-pages for quick navigation back -->
        <div
          class="d-inline-block"
          v-if="routeAppIs('reviews') && isReviewsRoute && route.name !== 'ReviewsLanding'"
        >
          <v-btn icon variant="text" @click="router.push('/reviews')">
            <v-icon color="yellow">mdi-star</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">Reviews Home</v-tooltip>
        </div>

        <!-- HR home button — shown on HR employees page for quick navigation back -->
        <div class="d-inline-block" v-if="route.name === 'HrEmployees'">
          <v-btn icon variant="text" @click="router.push('/hr')">
            <v-icon color="red-lighten-2">mdi-account-box</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">HR Home</v-tooltip>
        </div>

        <!-- Marketing home button — shown on disputes page for quick navigation back -->
        <div class="d-inline-block" v-if="route.name === 'DisputesHome'">
          <v-btn icon variant="text" @click="router.push('/marketing')">
            <v-icon color="orange">mdi-bullhorn</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">Marketing Home</v-tooltip>
        </div>

        <!-- Dynamic action buttons injected by pages via useAppBarActions -->
        <template v-for="action in visibleActions" :key="action.tooltip">
          <div class="d-inline-block">
            <v-btn
              icon
              :variant="action.variant || 'text'"
              @click="action.onClick"
              :loading="action.loading"
              :color="action.color"
            >
              <v-badge
                v-if="action.badge"
                :content="action.badge.content"
                :model-value="action.badge.show !== false"
                :color="action.badge.color || 'error'"
                :dot="action.badge.dot"
                floating
                class="app-bar-badge"
              >
                <v-icon>{{ action.icon }}</v-icon>
              </v-badge>
              <v-icon v-else>{{ action.icon }}</v-icon>
            </v-btn>
            <v-tooltip activator="parent" location="bottom">{{ action.tooltip }}</v-tooltip>
          </div>
        </template>

        <!-- Marketing (reviews) three-dot menu — shown on reviews pages within the marketing app -->
        <v-btn
          icon
          variant="text"
          @click="showReviewsMenu = true"
          v-if="routeAppIs('reviews') && isReviewsRoute && isAdmin"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Disputes three-dot menu — shown on disputes page -->
        <v-btn
          icon
          variant="text"
          @click="showDisputesMenu = true"
          v-if="route.name === 'DisputesHome'"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Contacts three-dot menu (admin only) -->
        <v-btn
          icon
          variant="text"
          @click="showContactsMenu = true"
          v-if="routeAppIs('contacts') && isAdmin"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Menu dots menu (renamed from Food Cost) -->
        <v-btn icon variant="text" @click="showMenuMenu = true" v-if="routeAppIs('menu')">
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Warehouse dots menu -->
        <v-btn
          icon
          variant="text"
          @click="showWarehouseMenu = true"
          v-if="routeAppIs('warehouse') && isWarehouseRoute && isAdmin"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Petty Cash dots menu (accounting app, petty cash sub-routes) -->
        <template v-if="routeAppIs('pettycash') && isPettyCashRoute && isAdmin">
          <v-btn icon variant="text" @click="showPettyCashMenu = true">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <!-- Journal dots menu -->
        <v-btn
          v-if="isJournalRoute && isAdmin"
          icon
          variant="text"
          @click="showJournalMenu = true"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
    </template>
  </v-app-bar>
</template>

<script setup>
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useApp } from "@/composables/useApp.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";
import { usePettyCash } from "@/composables/usePettyCash.js";
import { useAuditTotalLogs } from "@/composables/useAuditTotalLogs.js";
import WarehouseMonthPicker from "@/components/WarehouseMonthPicker.vue";
import PettyCashMonthPicker from "@/components/PettyCashMonthPicker.vue";

// Singleton — written by AuditLogs.vue, read here for the title chip
const { totalLogs: auditTotalLogsRef } = useAuditTotalLogs();
const auditTotalLogs = computed(() => auditTotalLogsRef.value);

const route = useRoute();
const router = useRouter();
const { smAndDown } = useDisplay();
const {
  isAuthenticated,
  isAdmin,
  showMainMenu,
  showReviewsMenu,
  showPettyCashMenu,
  showMenuMenu,
  showContactsMenu,
  showWarehouseMenu,
  showDisputesMenu,
  showJournalMenu,
  drawer,
} = useApp();
const { actions } = useAppBarActions();

// Helper: check if current route's meta.app matches a given app name (handles string or array)
const routeAppIs = (app) => {
  const metaApp = route.meta?.app;
  if (Array.isArray(metaApp)) return metaApp.includes(app);
  return metaApp === app;
};

// Reviews pages within the marketing app (for showing the reviews dot menu)
const reviewsRouteNames = [
  "ReviewsLanding",
  "ReviewsHome",
  "ReviewsToday",
  "ReviewsCalendar",
  "ReviewsClassic",
  "ReviewsImport",
  "ReviewsAdmin",
  "ReviewAssignments",
];
const isReviewsRoute = computed(() => reviewsRouteNames.includes(route.name));

// Settings sub-pages (all routes under /settings/* except the main Settings page itself)
const settingsRouteNames = [
  "UsersManagement",
  "AuditLogs",
  "RestaurantMappings",
  // "PlatformColumnMappings", // disabled — file import removed
  "BusinessRules",
  "OptionsManagement",
  // "PlatformCredentials", // disabled — file import removed
  "AssetSettings",
  "TagsManagement",
  "AccountManagersManagement",
  "AddressBookSettings",
  "ReviewsAdmin",
  "Documentation",
];
const isSettingsRoute = computed(() => settingsRouteNames.includes(route.name));

// Sales and petty cash sub-route detection within accounting app
const salesRouteNames = ["SalesHome", "SalesAnalytics", "VisieraOrders"];
const pettyCashRouteNames = [
  "PettyCashDaybook",
  "PettyCashLedger",
  "PettyCashJournal",
  "PettyCashEmployees",
];
const warehouseRouteNames = [
  "WarehouseInventory",
  "WarehouseJournal",
  "WarehouseLedger",
  "WarehouseParStock",
];
const isSalesRoute = computed(() => salesRouteNames.includes(route.name));
const isPettyCashRoute = computed(() => pettyCashRouteNames.includes(route.name));
const isWarehouseRoute = computed(() => warehouseRouteNames.includes(route.name));
const journalRouteNames = ["Journal", "Ledger", "Coa", "TrialBalance", "Financials", "Periods"];
const isJournalRoute = computed(() => journalRouteNames.includes(route.name));

// App-level config (used when no specific route config matches)
const appConfig = {
  accounting: { title: "Accounting", icon: "mdi-calculator-variant" },
  marketing: { title: "Marketing", icon: "mdi-bullhorn" },
  assets: { title: "Assets", icon: "mdi-folder-multiple-image" },
  contacts: { title: "Contacts", icon: "mdi-account-box", iconColor: "red-lighten-2" },
  menu: { title: "Menu", icon: "mdi-silverware-fork-knife", iconColor: "yellow" },
  warehouse: { title: "Warehouse", icon: "mdi-warehouse" },
  hr: { title: "HR and Contacts", icon: "mdi-file-document-multiple" },
};

// Per-route title overrides
const settingsEntry = { title: "Settings", icon: "mdi-cog" };
const accountEntry = { title: "Account", icon: "mdi-account-cog" };
const salesEntry = { icon: "mdi-chart-bar", iconColor: "blue" };
const routeConfig = {
  Home: { title: "Home", icon: "mdi-home" },
  AccountingHome: { title: "Accounting", icon: "mdi-calculator-variant" },
  MarketingHome: { title: "Marketing", icon: "mdi-bullhorn" },
  Settings: settingsEntry,
  UsersManagement: { title: "User Management", icon: "mdi-account-group" },
  AuditLogs: { title: "Audit Logs", icon: "mdi-history" },
  RestaurantMappings: { title: "Restaurant Mappings", icon: "mdi-cog" },
  // PlatformColumnMappings: { title: "Platform Formats", icon: "mdi-cog" }, // disabled — file import removed
  BusinessRules: { title: "Platform & Location Rules", icon: "mdi-cog" },
  OptionsManagement: { title: "Options", icon: "mdi-cog" },
  // PlatformCredentials: { title: "Platform Credentials", icon: "mdi-cog" }, // disabled — file import removed
  TagsManagement: { title: "Tags", icon: "mdi-cog" },
  ReviewsLanding: { title: "Reviews", icon: "mdi-star", iconColor: "yellow" },
  ReviewsToday: { title: "Today's Assignments", icon: "mdi-star", iconColor: "yellow" },
  ReviewsHome: { title: "Review Assignments", icon: "mdi-star", iconColor: "yellow" },
  AddressBookSettings: { title: "Address Book Settings", icon: "mdi-cog-outline" },
  ReviewAssignments: { title: "Assignments", icon: "mdi-clipboard-check-outline" },
  ReviewsCalendar: { title: "Assignments Calendar", icon: "mdi-calendar" },
  ReviewsAdmin: { title: "Reviews", icon: "mdi-database-edit-outline" },
  AccountManagersManagement: { title: "Account Managers", icon: "mdi-cog" },
  ApiInterface: { title: "API Interface", icon: "mdi-api" },
  Documentation: { title: "Documentation", icon: "mdi-book-open-variant" },
  Account: accountEntry,
  ChangePassword: accountEntry,
  MenuHome: { title: "Menu", icon: "mdi-silverware-fork-knife", iconColor: "indigo" },
  MenuIngredients: { title: "Ingredients", icon: "mdi-food-apple", iconColor: "green" },
  MenuRecipes: { title: "Recipes", icon: "mdi-file-document-outline", iconColor: "orange" },
  MenuPackaging: { title: "Packaging", icon: "mdi-package-variant-closed", iconColor: "blue" },
  MenuCategories: { title: "Categories", icon: "mdi-tag-multiple", iconColor: "purple" },
  MenuCombos: { title: "Combos", icon: "mdi-food-variant", iconColor: "teal" },
  Delivery: { title: "Delivery", icon: "mdi-truck-delivery", iconColor: "green-darken-3" },
  WarehouseInventory: { title: "Inventory", icon: "mdi-warehouse", iconColor: "green-darken-3" },
  WarehouseJournal: {
    title: "Stock Journal",
    icon: "mdi-book-open-page-variant",
    iconColor: "blue",
  },
  WarehouseLedger: { title: "Stock Ledger", icon: "mdi-book-open-variant", iconColor: "purple" },
  WarehouseParStock: { title: "Par Stock", icon: "mdi-chart-bar", iconColor: "warning" },
  PettyCashDaybook: { title: "Petty Cash", icon: "mdi-cash-multiple", iconColor: "green-darken-3" },
  PettyCashLedger: { title: "Petty Cash", icon: "mdi-cash-multiple", iconColor: "green-darken-3" },
  PettyCashJournal: { title: "Petty Cash", icon: "mdi-cash-multiple", iconColor: "green-darken-3" },
  PettyCashEmployees: {
    title: "Petty Cash",
    icon: "mdi-cash-multiple",
    iconColor: "green-darken-3",
  },
  ContactsCredentials: { title: "Credentials", icon: "mdi-shield-key" },
  HrHome: { title: "HR and Contacts", icon: "mdi-file-document-multiple" },
  HrEmployees: { title: "HR Employees", icon: "mdi-account-group" },
  CampaignsHome: { title: "Campaigns", icon: "mdi-bullhorn" },
  CampaignsCalendar: { title: "Campaign Calendar", icon: "mdi-calendar" },
  CampaignsWeeklyCalendar: { title: "Weekly Calendar", icon: "mdi-calendar" },
  DisputesHome: { title: "Disputes", icon: "mdi-file-document-outline", iconColor: "orange" },
  SalesHome: { title: "Sales", ...salesEntry },
  SalesAnalytics: { title: "Sales Analytics", icon: "mdi-chart-line" },
  VisieraOrders: { title: "Manage Sales Data", icon: "mdi-database-edit-outline" },
  Journal: { title: "Journal", icon: "mdi-book-open-page-variant", iconColor: "teal" },
  Ledger: { title: "Ledger", icon: "mdi-book-open-variant", iconColor: "teal" },
  Coa: { title: "Chart of Accounts", icon: "mdi-file-tree", iconColor: "teal" },
  TrialBalance: { title: "Trial Balance", icon: "mdi-scale-balance", iconColor: "teal" },
  Financials: { title: "Financials", icon: "mdi-file-document-outline", iconColor: "teal" },
  Periods: { title: "Periods", icon: "mdi-calendar-lock", iconColor: "teal" },
};

// Resolve current page config
const currentAppConfig = computed(() => routeConfig[route.name] || appConfig[route.meta?.app]);
const title = computed(() => currentAppConfig.value?.title || "Pizza Felice LLC");

// Petty cash sub-view state
const {
  openCreateDialog: pettyCashAdd,
  activeView: pettyCashView,
  fetchEntries,
  fetchPeriodStatus,
} = usePettyCash();

const pettyCashViews = {
  ledger: { title: "Ledger", icon: "mdi-book-open-page-variant" },
  journal: { title: "Journal", icon: "mdi-book-edit-outline" },
  employees: { title: "Employee Balances", icon: "mdi-account-cash" },
};
const pettyCashViewTitle = computed(() => pettyCashViews[pettyCashView.value]?.title || "");
const pettyCashViewIcon = computed(() => pettyCashViews[pettyCashView.value]?.icon || "");

// Filtered actions (respect show flag)
const visibleActions = computed(() => actions.value.filter((a) => a.show !== false && !a.chip));
// Chip-style action shown inline in the title (e.g. sales analytics record count)
const salesAnalyticsChip = computed(() => actions.value.find((a) => a.chip));
</script>

<style scoped>
/* Remove default left margin on toolbar title */
:deep(.v-toolbar-title) {
  margin-inline-start: 0 !important;
}
/* Tighter gap between action buttons */
:deep(.v-toolbar__append) {
  gap: 4px;
}
/* Compact floating badge */
.app-bar-badge :deep(.v-badge__badge) {
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 10px;
  top: -2px !important;
}
</style>
